import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, Pencil, FileText, AlertCircle, CheckCircle2, Clock } from "lucide-react";

const STATUS_LABEL = { pending:"معلق", deposited:"مودع", cleared:"تم صرفه", bounced:"مرتجع", cancelled:"ملغى" };
const STATUS_COLOR = { pending:"bg-amber-100 text-amber-800", deposited:"bg-blue-100 text-blue-800", cleared:"bg-emerald-100 text-emerald-800", bounced:"bg-red-100 text-red-800", cancelled:"bg-slate-100 text-slate-800" };

export default function Banks() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [checks, setChecks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acForm, setAcForm] = useState({ name:"", bank_name:"", account_number:"", iban:"", currency:"SYP", opening_balance:0, notes:"" });
  const [acEditing, setAcEditing] = useState(null);
  const [chkOpen, setChkOpen] = useState(false);
  const [chkForm, setChkForm] = useState({ kind:"received", check_number:"", bank_name:"", amount:0, currency:"SYP", party_id:"", party_name:"", maturity_date: new Date().toISOString().slice(0,10), status:"pending", bank_account_id:"", notes:"" });
  const [chkEditing, setChkEditing] = useState(null);

  const load = async () => {
    try {
      const [a, c, cu, su] = await Promise.all([
        api.get("/bank-accounts"), api.get("/checks"),
        api.get("/customers").catch(()=>({data:[]})),
        api.get("/suppliers").catch(()=>({data:[]})),
      ]);
      setAccounts(a.data); setChecks(c.data); setCustomers(cu.data); setSuppliers(su.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  // ---- Bank Account CRUD ----
  const openAc = (a) => { setAcEditing(a); setAcForm(a ? {...a} : { name:"", bank_name:"", account_number:"", iban:"", currency:"SYP", opening_balance:0, notes:"" }); setAcOpen(true); };
  const saveAc = async () => {
    try {
      if (acEditing) await api.put(`/bank-accounts/${acEditing.id}`, acForm);
      else await api.post("/bank-accounts", acForm);
      toast.success("تم الحفظ"); setAcOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delAc = async (a) => {
    if (!confirm(`حذف الحساب البنكي "${a.name}"?`)) return;
    try { await api.delete(`/bank-accounts/${a.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  // ---- Check CRUD ----
  const openChk = (c) => {
    setChkEditing(c);
    setChkForm(c ? {...c} : { kind:"received", check_number:"", bank_name:"", amount:0, currency:"SYP", party_id:"", party_name:"", maturity_date: new Date().toISOString().slice(0,10), status:"pending", bank_account_id:"", notes:"" });
    setChkOpen(true);
  };
  const saveChk = async () => {
    if (!chkForm.check_number || !chkForm.party_name || !chkForm.amount || !chkForm.maturity_date) return toast.error("املأ جميع الحقول الأساسية");
    try {
      const body = {...chkForm, amount: +chkForm.amount, party_id: chkForm.party_id || null, bank_account_id: chkForm.bank_account_id || null};
      if (chkEditing) await api.put(`/checks/${chkEditing.id}`, body);
      else await api.post("/checks", body);
      toast.success("تم الحفظ"); setChkOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delChk = async (c) => {
    if (!confirm(`حذف الشيك #${c.check_number}?`)) return;
    try { await api.delete(`/checks/${c.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const changeStatus = async (c, newStatus) => {
    try {
      const body = {...c, status: newStatus};
      await api.put(`/checks/${c.id}`, body);
      toast.success("تم التحديث");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const parties = chkForm.kind === "received" ? customers : suppliers;
  const dueChecks = checks.filter(c => c.status === "pending" && (c.overdue || c.due_soon));

  return (
    <div className="space-y-6" data-testid="banks-page">
      <div className="flex items-center gap-2">
        <Landmark className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">الحسابات البنكية والشيكات</h1>
          <p className="text-sm text-muted-foreground">حسابات بنكية منفصلة + إدارة شيكات مستلمة/مصدرة بتواريخ الاستحقاق</p>
        </div>
      </div>

      {dueChecks.length > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-300" data-testid="due-checks-alert">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4" />
            لديك <b>{dueChecks.length}</b> شيك يستحق قريباً أو متأخر — راجع التبويب الثاني
          </div>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="accounts" data-testid="tab-accounts">الحسابات البنكية ({accounts.length})</TabsTrigger>
          <TabsTrigger value="checks" data-testid="tab-checks">الشيكات ({checks.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "accounts" && (
        <>
          <div className="flex justify-end"><Button onClick={()=>openAc(null)} className="gap-1" data-testid="add-account-btn"><Plus className="w-4 h-4" /> حساب بنكي جديد</Button></div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">الاسم</TableHead><TableHead className="text-start">البنك</TableHead>
                <TableHead className="text-start">IBAN</TableHead><TableHead className="text-end">الرصيد</TableHead>
                <TableHead className="text-start">العملة</TableHead><TableHead className="text-end">إجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {accounts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">لا توجد حسابات بنكية — أنشئ أول حساب</TableCell></TableRow>}
                {accounts.map(a => (
                  <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                    <TableCell className="font-semibold">{a.name}</TableCell>
                    <TableCell>{a.bank_name}</TableCell>
                    <TableCell className="text-xs font-mono">{a.iban || a.account_number || "—"}</TableCell>
                    <TableCell className="text-end tabular-nums font-bold text-emerald-700">{(a.balance || 0).toLocaleString("ar-EG")}</TableCell>
                    <TableCell className="text-xs">{a.currency}</TableCell>
                    <TableCell className="text-end">
                      <Button size="sm" variant="ghost" onClick={()=>openAc(a)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>delAc(a)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {tab === "checks" && (
        <>
          <div className="flex justify-end"><Button onClick={()=>openChk(null)} className="gap-1" data-testid="add-check-btn"><Plus className="w-4 h-4" /> شيك جديد</Button></div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">النوع</TableHead><TableHead className="text-start">رقم الشيك</TableHead>
                <TableHead className="text-start">البنك</TableHead><TableHead className="text-start">الطرف</TableHead>
                <TableHead className="text-end">المبلغ</TableHead><TableHead className="text-start">الاستحقاق</TableHead>
                <TableHead className="text-start">الحالة</TableHead><TableHead className="text-end">إجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {checks.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">لا توجد شيكات</TableCell></TableRow>}
                {checks.map(c => (
                  <TableRow key={c.id} className={c.overdue ? "bg-red-50/40" : c.due_soon ? "bg-amber-50/40" : ""} data-testid={`check-row-${c.id}`}>
                    <TableCell>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.kind === "received" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                        {c.kind === "received" ? "مستلم" : "مصدر"}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono">#{c.check_number}</TableCell>
                    <TableCell className="text-xs">{c.bank_name}</TableCell>
                    <TableCell className="text-xs">{c.party_name}</TableCell>
                    <TableCell className="text-end tabular-nums font-bold">{c.amount.toLocaleString("ar-EG")} {c.currency}</TableCell>
                    <TableCell className="text-xs">
                      {c.maturity_date}
                      {c.overdue && <span className="ms-1 text-red-600 text-[10px] font-bold">متأخر</span>}
                      {c.due_soon && !c.overdue && <span className="ms-1 text-amber-600 text-[10px] font-bold flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />قريب</span>}
                    </TableCell>
                    <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</span></TableCell>
                    <TableCell className="text-end">
                      {c.status === "pending" && <Button size="sm" variant="ghost" onClick={()=>changeStatus(c, "cleared")} className="text-emerald-600" title="تم صرفه" data-testid={`clear-check-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>}
                      <Button size="sm" variant="ghost" onClick={()=>openChk(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>delChk(c)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Account dialog */}
      <Dialog open={acOpen} onOpenChange={setAcOpen}>
        <DialogContent className="max-w-md" data-testid="account-dialog">
          <DialogHeader><DialogTitle>{acEditing ? "تعديل حساب بنكي" : "حساب بنكي جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>الاسم</Label><Input value={acForm.name} onChange={(e)=>setAcForm({...acForm, name:e.target.value})} data-testid="ac-name" autoFocus /></div>
            <div className="col-span-2"><Label>اسم البنك</Label><Input value={acForm.bank_name} onChange={(e)=>setAcForm({...acForm, bank_name:e.target.value})} data-testid="ac-bank" /></div>
            <div><Label>رقم الحساب</Label><Input value={acForm.account_number} onChange={(e)=>setAcForm({...acForm, account_number:e.target.value})} /></div>
            <div><Label>IBAN</Label><Input value={acForm.iban} onChange={(e)=>setAcForm({...acForm, iban:e.target.value})} className="font-mono" /></div>
            <div><Label>العملة</Label>
              <Select value={acForm.currency} onValueChange={(v)=>setAcForm({...acForm, currency:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SYP">SYP</SelectItem><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
              </Select>
            </div>
            {!acEditing && <div><Label>الرصيد الافتتاحي</Label><Input type="number" value={acForm.opening_balance} onChange={(e)=>setAcForm({...acForm, opening_balance:+e.target.value})} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setAcOpen(false)}>إلغاء</Button><Button onClick={saveAc} data-testid="ac-save-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check dialog */}
      <Dialog open={chkOpen} onOpenChange={setChkOpen}>
        <DialogContent className="max-w-lg" data-testid="check-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />{chkEditing ? "تعديل شيك" : "شيك جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>النوع</Label>
              <Select value={chkForm.kind} onValueChange={(v)=>setChkForm({...chkForm, kind:v, party_id:"", party_name:""})}>
                <SelectTrigger data-testid="chk-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="received">مستلم من عميل</SelectItem><SelectItem value="issued">مصدر لمورد</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>الحالة</Label>
              <Select value={chkForm.status} onValueChange={(v)=>setChkForm({...chkForm, status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_LABEL).map(k=><SelectItem key={k} value={k}>{STATUS_LABEL[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>رقم الشيك</Label><Input value={chkForm.check_number} onChange={(e)=>setChkForm({...chkForm, check_number:e.target.value})} data-testid="chk-number" className="font-mono" /></div>
            <div><Label>اسم البنك</Label><Input value={chkForm.bank_name} onChange={(e)=>setChkForm({...chkForm, bank_name:e.target.value})} data-testid="chk-bank" /></div>
            <div className="col-span-2"><Label>{chkForm.kind === "received" ? "العميل" : "المورد"}</Label>
              <Select value={chkForm.party_id || undefined} onValueChange={(v)=>{ const p = parties.find(x=>x.id===v); setChkForm({...chkForm, party_id:v, party_name: p?.name || ""}); }}>
                <SelectTrigger data-testid="chk-party"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{parties.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>المبلغ</Label><Input type="number" value={chkForm.amount} onChange={(e)=>setChkForm({...chkForm, amount:e.target.value})} data-testid="chk-amount" /></div>
            <div><Label>العملة</Label>
              <Select value={chkForm.currency} onValueChange={(v)=>setChkForm({...chkForm, currency:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SYP">SYP</SelectItem><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>تاريخ الاستحقاق</Label><Input type="date" value={chkForm.maturity_date} onChange={(e)=>setChkForm({...chkForm, maturity_date:e.target.value})} data-testid="chk-maturity" /></div>
            <div><Label>حساب الإيداع (عند التصفية)</Label>
              <Select value={chkForm.bank_account_id || "__none__"} onValueChange={(v)=>setChkForm({...chkForm, bank_account_id: v === "__none__" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— بدون —</SelectItem>
                  {accounts.filter(a=>a.currency===chkForm.currency).map(a=><SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>ملاحظات</Label><Input value={chkForm.notes} onChange={(e)=>setChkForm({...chkForm, notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setChkOpen(false)}>إلغاء</Button><Button onClick={saveChk} data-testid="chk-save-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
